// Module registry metadata (Entitlements Mega PR A batch A1).
//
// `MODULE_KEYS` (module-keys.ts) is the canonical flat list of every
// entitlement key. `MODULE_REGISTRY` adds the per-module metadata the rest
// of the entitlements system needs but the flat list can't carry:
//
//   - `category`     UI grouping for the admin role-builder / entitlements panel.
//   - `risk`         Sensitivity tier. `critical` modules require a non-empty
//                    `reason` on every grant (mutation-engine write-time rule,
//                    master plan section 4.4).
//   - `dependsOn`    Hard module coupling. Enabling a module requires every
//                    `dependsOn` target to resolve `view|full` in the projected
//                    post-change state; disabling a module is rejected while an
//                    enabled dependent still points at it. The runtime resolver
//                    NEVER recurses — the write-time validator is the gatekeeper.
//   - `visibility`   `public` (GA tenant-facing toggle) / `beta` (flag-gated or
//                    pre-GA) / `internal` (platform substrate, not a tenant toggle).
//   - `defaultGranted` Mirrors `DEFAULT_GRANTED_MODULE_KEYS`: a tenant with NO
//                    explicit row is entitled unless it sets an explicit
//                    `off`/`locked`. `check-entitlement-registry.cjs` fails if
//                    this drifts from that set.
//   - `deprecatedInVersion` Set when a key is retired. Keys are NEVER deleted
//                    (audit logs + JWT claims carry them forever); mark them
//                    deprecated and leave them in `MODULE_KEYS`.
//   - `comingSoon`   Module exists in the registry (roadmap/planning display)
//                    but isn't buildable/activatable yet -- distinct from
//                    `deprecatedInVersion` (retired, opposite lifecycle
//                    direction) and `visibility: 'beta'` (flag-gated but
//                    already activatable today).
//
// Invariants (completeness, resolvable deps, acyclic graph, defaultGranted
// parity, deprecate-don't-delete) are enforced by the blocking gate
// `scripts/check-entitlement-registry.cjs`. The `satisfies Record<ModuleKey, ...>`
// below is the compile-time half: every key must have exactly one entry.
//
// Dependency edges are verified against ACTUAL module coupling (runtime
// imports / workspace package deps), not aspirational product structure:
//   ap → financials         modules/ap imports @heyroot/financials/api-interface
//   budgets → financials    modules/budgets takes FinancialsApi as a hard ctor dep
//   house-accounts → ar     modules/house-accounts imports @heyroot/ar/api-interface
// ar→financials and fixed-assets→financials are deliberately NOT edges: those
// modules only record a GL FK as back-filled text and run standalone, so a hard
// "can't disable financials" edge would be wrong (verified 2026-07-04).
//
// kds → pos_fnb (2026-07-10, deliberate exception to "matches real imports"):
// modules/kds's actual import is @heyroot/pos/api-interface (unchanged —
// @heyroot/pos remains one shared package serving both POS surfaces; this
// entitlement split does not divide the package). This edge intentionally
// points at pos_fnb instead for a correctness reason, not a product-taste
// reason: 'pos' is deprecated and blocked from new grants (see module-keys.ts),
// so a dependsOn: ['pos'] edge would make kds permanently unobtainable for
// every tenant provisioned after this change. pos_fnb is the live, grantable
// key, and KDS is meaningless without F&B, so this is also the correct
// product statement. See docs/superpowers/specs/2026-07-10-pos-retail-fnb-
// coexistence-design.md §7.
/**
 * Per-module metadata, keyed by `ModuleKey`. The `satisfies Record<ModuleKey,
 * ModuleRegistryEntry>` makes a missing or orphan key a compile error; the
 * gate re-checks at CI time (belt and braces — the barrel is imported by JS
 * consumers that don't type-check the contracts source).
 */
export const MODULE_REGISTRY = {
    // ── Commerce ──
    commerce: {
        category: 'commerce',
        risk: 'high',
        dependsOn: [],
        visibility: 'public',
        defaultGranted: false,
    },
    pos: {
        category: 'commerce',
        risk: 'high',
        dependsOn: [],
        visibility: 'public',
        defaultGranted: false,
        deprecatedInVersion: '2026-07-10-pos-retail-fnb-split',
    },
    pos_retail: {
        category: 'commerce',
        risk: 'high',
        dependsOn: [],
        visibility: 'public',
        defaultGranted: false,
        label: 'Retail POS',
    },
    pos_fnb: {
        category: 'commerce',
        risk: 'high',
        dependsOn: [],
        visibility: 'public',
        defaultGranted: false,
        label: 'Food & Beverage POS',
    },
    kds: {
        category: 'commerce',
        risk: 'medium',
        dependsOn: ['pos_fnb'],
        visibility: 'public',
        defaultGranted: false,
    },
    catalog: {
        category: 'commerce',
        risk: 'medium',
        dependsOn: [],
        visibility: 'public',
        defaultGranted: false,
    },
    printers: {
        category: 'commerce',
        risk: 'low',
        dependsOn: [],
        visibility: 'public',
        defaultGranted: false,
    },
    receipts: {
        category: 'commerce',
        risk: 'medium',
        dependsOn: [],
        visibility: 'beta',
        defaultGranted: false,
    },
    'cash-handling': {
        category: 'commerce',
        risk: 'high',
        dependsOn: [],
        visibility: 'beta',
        defaultGranted: false,
    },
    'gift-cards': {
        category: 'commerce',
        risk: 'critical',
        dependsOn: [],
        visibility: 'public',
        defaultGranted: false,
    },
    // ── Financials ──
    financials: {
        category: 'financials',
        risk: 'critical',
        dependsOn: [],
        visibility: 'public',
        defaultGranted: false,
    },
    ap: {
        category: 'financials',
        risk: 'critical',
        dependsOn: ['financials'],
        visibility: 'public',
        defaultGranted: false,
    },
    ar: {
        category: 'financials',
        risk: 'critical',
        dependsOn: [],
        visibility: 'public',
        defaultGranted: false,
    },
    'house-accounts': {
        category: 'financials',
        risk: 'high',
        dependsOn: ['ar'],
        visibility: 'public',
        defaultGranted: false,
    },
    'fixed-assets': {
        category: 'financials',
        risk: 'high',
        dependsOn: [],
        visibility: 'public',
        defaultGranted: false,
    },
    'tax-1099-filing': {
        category: 'financials',
        risk: 'high',
        dependsOn: [],
        visibility: 'public',
        defaultGranted: false,
    },
    budgets: {
        category: 'financials',
        risk: 'high',
        dependsOn: ['financials'],
        visibility: 'public',
        defaultGranted: false,
    },
    tax: {
        category: 'financials',
        risk: 'high',
        dependsOn: [],
        visibility: 'public',
        // 2026-08-14: moved to DEFAULT_GRANTED_MODULE_KEYS (see that file's
        // comment) -- removed from plan-registry.ts's STARTER_CORE to satisfy
        // the binding invariant tested in plan-registry.test.ts.
        defaultGranted: true,
    },
    taxes: {
        category: 'financials',
        risk: 'high',
        dependsOn: [],
        visibility: 'public',
        defaultGranted: false,
    },
    billing: {
        category: 'financials',
        risk: 'critical',
        dependsOn: [],
        visibility: 'internal',
        defaultGranted: false,
    },
    'merchant-services': {
        category: 'financials',
        risk: 'critical',
        dependsOn: [],
        visibility: 'internal',
        defaultGranted: true,
    },
    // ── Workforce ──
    workforce: {
        category: 'workforce',
        risk: 'high',
        dependsOn: [],
        visibility: 'public',
        defaultGranted: false,
    },
    'payroll-providers': {
        category: 'workforce',
        risk: 'critical',
        dependsOn: [],
        visibility: 'public',
        defaultGranted: false,
    },
    mileage: {
        category: 'workforce',
        risk: 'medium',
        dependsOn: [],
        visibility: 'public',
        defaultGranted: false,
    },
    // ── Hospitality ──
    lodging: {
        category: 'hospitality',
        risk: 'high',
        dependsOn: [],
        visibility: 'public',
        defaultGranted: false,
    },
    reservations: {
        category: 'hospitality',
        risk: 'medium',
        dependsOn: [],
        visibility: 'public',
        defaultGranted: false,
    },
    // Appointments (S7) -- scheduling/booking kernel, same category+risk+
    // dependsOn shape as reservations (its nearest analog: both are
    // booking-against-real-catalog-data systems that run standalone per the
    // ar/fixed-assets precedent above, not a hard module-coupling edge).
    appointments: {
        category: 'hospitality',
        risk: 'medium',
        dependsOn: [],
        visibility: 'public',
        defaultGranted: false,
    },
    'room-layouts': {
        category: 'hospitality',
        risk: 'low',
        dependsOn: [],
        visibility: 'beta',
        defaultGranted: true,
    },
    dining: {
        category: 'hospitality',
        risk: 'medium',
        // room-layouts: hard edge — modules/dining/api-interface/index.ts
        // hard-imports getRoomLocation from @heyroot/room-layouts/api-interface
        // with fail-closed behavior on mutations (WaitlistRoomResolutionUnavailableError
        // on failure) — real, verified coupling per this file's own methodology.
        // room-layouts is defaultGranted: true, so this edge is trivially
        // satisfied for every tenant already — zero friction on re-activation.
        //
        // Deliberately NOT 'pos_fnb' or 'reservations':
        //   - pos_fnb: modules/dining/package.json has NO workspace dependency on
        //     @heyroot/pos or @heyroot/pos_fnb at all. Unlike kds->pos_fnb (which
        //     traces to a real @heyroot/pos package dependency — kds tickets ARE
        //     pos orders), "dining is F&B, pos_fnb is F&B" is a product-taxonomy
        //     argument, not verified code coupling. Rejected during review.
        //   - reservations: modules/dining/api-interface/index.ts:~503-557 wraps
        //     reservationsService.upcomingClaims() in a 300ms race that degrades
        //     to `{ claims: 0, degraded: true }` on timeout/error — soft
        //     coupling, matching the ar->financials non-edge precedent already
        //     documented above in this file.
        dependsOn: ['room-layouts'],
        visibility: 'beta',
        defaultGranted: false,
    },
    // customer-billing (Plan D / recurring-billing): groups customer_billing.*
    // permissions and gates the billing Overview / exceptions routes
    // (entitlement: 'customer-billing'). Starter-tier since 2026-10-09 as the
    // simple onboarding path's "Recurring billing" choice (plan-registry.ts).
    'customer-billing': {
        category: 'commerce',
        risk: 'medium',
        dependsOn: [],
        visibility: 'public',
        defaultGranted: false,
        label: 'Customer Billing',
    },
    // Virtual Terminal module (modules/payments) -- card-only Quick Charge /
    // Send-a-link collection surface (VT PR-2). Pilot-only per
    // virtual-terminal-CONTRACT.md's explicit gating requirement:
    // defaultGranted: false, not a piggyback on pos_retail/pos_fnb (those
    // gate unrelated feature sets -- Decision 4, PR-2 design doc).
    virtual_terminal: {
        category: 'commerce',
        risk: 'medium',
        dependsOn: [],
        visibility: 'public',
        defaultGranted: false,
        label: 'Virtual Terminal',
    },
    // Customer Display module (apps/customer-apps + modules/pos/api-interface/
    // customer-display.ts) -- retail Pusher/tablet pairing display (PR-5).
    // defaultGranted: false, not bundled into any PLAN_REGISTRY tier (see
    // plan-registry.test.ts's PARKED_FROM_PLANS) -- admin-grantable only,
    // matching pos_fnb's posture exactly.
    pos_customer_display: {
        category: 'commerce',
        risk: 'medium',
        dependsOn: [],
        visibility: 'public',
        defaultGranted: false,
        label: 'Customer Display',
    },
    // Pickup Board (modules/pos) -- the pickup/online check worklist. PARKED
    // 2026-09-03 (ticket #10716); revisit in a few months. defaultGranted:
    // false and not bundled into any PLAN_REGISTRY tier (see
    // plan-registry.test.ts's PARKED_FROM_PLANS) -- admin-grantable only,
    // matching pos_customer_display's posture immediately above.
    //
    // dependsOn is deliberately [] even though Dining's Host Stand reuses the
    // PickupBoard component (apps/backoffice HostStandPage): that is a frontend
    // component import, not an entitlement dependency, and registry.test.ts
    // asserts an exact hardcoded edge map for every non-empty dependsOn. NOTE
    // for whoever un-parks `dining`: grant this key too, or Host Stand's Pickup
    // tab will 402.
    pos_pickup_board: {
        category: 'commerce',
        risk: 'medium',
        dependsOn: [],
        visibility: 'public',
        defaultGranted: false,
        label: 'Pickup Board',
    },
    // SMS invoice delivery (ticket #9343, §10.2 of the plan doc) -- gates
    // requesting the `sms` channel for ar.invoice_issued/ar.invoice_reminder
    // on top of the existing email-only delivery. `category: 'commerce'`
    // matches the `customer-billing` module key's own category immediately
    // below (there is no `'customer-billing'` ModuleCategory literal --
    // confirmed by reading this file's ModuleCategory union before using it,
    // per the plan's own "confirm the exact category taxonomy at
    // implementation time" instruction). `risk: 'medium'` matches
    // `virtual_terminal`'s own risk tier (money-adjacent, third-party
    // carrier spend, customer PII). Orthogonal to (not a replacement for)
    // `channel_sending_mode`/`tenant_messaging_plan`, which still gate
    // whether a tenant can send SMS at all -- this key only gates whether
    // the tenant's PLAN may use the invoice-SMS feature.
    //
    // Update (ticket #9377): risk bumped from 'medium' to 'critical'. The
    // existing critical-reason mechanism (isCriticalModule -> a non-empty
    // reason required on grant/change, enforced by validateEntitlementChanges)
    // doubles as the #9377 attestation checkbox's audit evidence -- the reason
    // a staffer types when granting is the "supporting evidence" the legal
    // ruling called for, with zero new consent-capture UI.
    sms_invoice_delivery: {
        category: 'commerce',
        risk: 'critical',
        dependsOn: [],
        visibility: 'public',
        defaultGranted: false,
        label: 'Transactional SMS Delivery',
    },
    // quotes (Billing Quotes Phase 2): registered for PERMISSION_METADATA.module
    // grouping of quotes.* permissions, matching the customer-billing precedent
    // above -- entitlement wiring (plan-tier gating) is a follow-up for PR-3,
    // which adds the nav entry this entitlement will actually gate.
    quotes: {
        category: 'commerce',
        risk: 'medium',
        dependsOn: [],
        visibility: 'public',
        defaultGranted: false,
        label: 'Quotes',
    },
    // write-ups (Write-Ups module, Task 7 of the 2026-09-04 write-ups
    // implementation plan): registered for PERMISSION_METADATA.module grouping
    // of write-ups.* permissions, matching the quotes precedent immediately
    // above -- same category/risk/dependsOn/visibility/defaultGranted shape.
    'write-ups': {
        category: 'commerce',
        risk: 'medium',
        dependsOn: [],
        visibility: 'public',
        defaultGranted: false,
        label: 'Write-Ups',
    },
    // Document Templates module (Session 1 of the invoice/quote template
    // builder program, ADR-0174): invoice/quote layout template CRUD +
    // version pinning. `visibility: 'internal'` follows the `inbox` precedent
    // below -- staged per-tenant enablement during rollout, not a tenant
    // self-service Modules-page toggle.
    'document-templates': {
        category: 'commerce',
        risk: 'low',
        dependsOn: [],
        visibility: 'internal',
        defaultGranted: false,
        label: 'Document Templates',
    },
    // Payment Hub module (docs/plans/root-payment-hub-handoff, ADR-0176): the
    // unified `payment_request` primitive collecting money for invoices,
    // accounts, orders, deposits, appointments, gift-card storefront checkout
    // and general reasons through one settlement dispatcher. `visibility:
    // 'beta'` because every `PAYMENT_HUB_*` flag ships off (there is no
    // `'tenant'` member of `ModuleVisibility`); `dependsOn: []` deliberately,
    // mirroring `virtual_terminal` below -- the Hub gates its own
    // money-collection surface, not a dependent of another module's
    // entitlement.
    payment_hub: {
        category: 'commerce',
        risk: 'high',
        dependsOn: [],
        visibility: 'beta',
        defaultGranted: false,
        label: 'Online Payments',
    },
    // ── CRM ──
    customers: {
        category: 'crm',
        risk: 'medium',
        dependsOn: [],
        visibility: 'public',
        defaultGranted: false,
    },
    loyalty: {
        category: 'crm',
        risk: 'medium',
        dependsOn: [],
        visibility: 'beta',
        defaultGranted: false,
    },
    memberships: {
        category: 'crm',
        risk: 'high',
        dependsOn: [],
        visibility: 'public',
        defaultGranted: false,
    },
    comms: {
        category: 'crm',
        risk: 'medium',
        dependsOn: [],
        visibility: 'public',
        defaultGranted: false,
    },
    // Operator Conversations Inbox (modules/comms) -- PARKED per
    // docs/superpowers/plans/2026-09-05-park-inbox-entitlement-gate.md.
    // `visibility: 'internal'`, deliberately NOT 'public' even though this
    // plan's own cited precedents (virtual_terminal, pos_customer_display,
    // pos_pickup_board) all use 'public': isEntryTenantActivatable
    // (modules/tenants/domain/module-activation-policy.ts) returns true
    // whenever visibility !== 'internal' (Inbox would pass every other
    // condition -- not default-granted, not deprecated, no comingSoon), and
    // shouldListModule lists any module passing that check on the
    // tenant-facing Settings > Modules self-service page, which is genuinely
    // live on staging today. 'public' would put Inbox on that page -- a
    // direct hit against the "not in settings" requirement this plan exists
    // to satisfy. 'internal' breaks no admin path: apps/admin's
    // tenant-entitlement write path and the Module Matrix have zero
    // references to `visibility` -- platform-admin grant/read is completely
    // visibility-agnostic. Flip to 'public' at reactivation ONLY if genuine
    // tenant self-service is intended -- never as a mechanical "turn it back
    // on" step.
    inbox: {
        category: 'crm',
        risk: 'medium',
        dependsOn: [],
        visibility: 'internal',
        defaultGranted: false,
    },
    // ── Platform / substrate ──
    integrations: {
        category: 'platform',
        risk: 'medium',
        dependsOn: [],
        visibility: 'public',
        defaultGranted: false,
    },
    audit: {
        category: 'platform',
        risk: 'low',
        dependsOn: [],
        visibility: 'internal',
        defaultGranted: false,
    },
    tenants: {
        category: 'platform',
        risk: 'low',
        dependsOn: [],
        visibility: 'internal',
        defaultGranted: false,
    },
    permissions: {
        category: 'platform',
        risk: 'low',
        dependsOn: [],
        visibility: 'internal',
        defaultGranted: false,
    },
    // Default-granted (ticket #10485) -- mirrors DEFAULT_GRANTED_MODULE_KEYS.
    insights: {
        category: 'platform',
        risk: 'medium',
        dependsOn: [],
        visibility: 'beta',
        defaultGranted: true,
    },
    // F&B reporting (modules/fnb-reporting) — S12 Phase-1 revenue read-model
    // reports (ADR-0139). No dependsOn edge: reads its own rm_fnb_* tables,
    // populated by an out-of-band event consumer, not a direct import of
    // @heyroot/commerce or @heyroot/pos (matches the ar/fixed-assets
    // reasoning above — event-sourced data, not hard module coupling).
    'fnb-reporting': {
        category: 'commerce',
        risk: 'medium',
        dependsOn: [],
        visibility: 'public',
        defaultGranted: false,
        label: 'F&B Reporting',
    },
    support: {
        category: 'platform',
        risk: 'low',
        dependsOn: [],
        visibility: 'beta',
        defaultGranted: true,
    },
    // ── Experience ──
    assistant: {
        category: 'experience',
        risk: 'low',
        dependsOn: [],
        visibility: 'beta',
        defaultGranted: true,
    },
    branding: {
        category: 'experience',
        risk: 'low',
        dependsOn: [],
        visibility: 'beta',
        defaultGranted: false,
    },
    // J12 (R-24 / R-23): the HeyRoot-recorded regulated profile, not a module. critical, so recording
    // one always carries a reason; internal and never default-granted, so no plan tier holds it. Only
    // the platform-admin regulated-profile lane writes it (entitlement-mutation.ts validator).
    regulated_profile: {
        category: 'platform',
        risk: 'critical',
        dependsOn: [],
        visibility: 'internal',
        defaultGranted: false,
        label: 'Regulated profile (HeyRoot-recorded)',
    },
};
/** Look up a module's registry entry. */
export function getModuleRegistryEntry(key) {
    return MODULE_REGISTRY[key];
}
/** The direct `dependsOn` targets of a module (never recursive). */
export function moduleDependsOn(key) {
    return MODULE_REGISTRY[key].dependsOn;
}
/** True if `key` is `risk: 'critical'` — grants require a non-empty reason. */
export function isCriticalModule(key) {
    return MODULE_REGISTRY[key].risk === 'critical';
}
/** True if `key` is deprecated (`deprecatedInVersion` set) — blocked from new grants. */
export function isDeprecatedModule(key) {
    return getModuleRegistryEntry(key).deprecatedInVersion != null;
}
/** Friendly display name for `key`, falling back to the raw key when no `label` is
 * set OR when `key` itself has no registry entry (a monorepo package-version skew --
 * e.g. a caller's compiled dist still emits a moduleKey a since-updated registry no
 * longer recognizes -- must degrade to the raw key, never throw: several callers use
 * this to render a user-facing entitlement notice where a thrown error would replace
 * a gracefully-degraded partial result with a total failure). getModuleRegistryEntry's
 * own return type asserts non-optional -- true only for a `key` the CURRENT registry
 * snapshot actually has; the cast below is what makes this function honest about the
 * cross-package-version case that assertion can't see. */
export function moduleLabel(key) {
    const entry = getModuleRegistryEntry(key);
    return entry?.label ?? key;
}
//# sourceMappingURL=registry.js.map